import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface GroupedCreatorFilters {
  platform?: string;
  searchTerm?: string;
  claimStatus?: string;
  limit?: number;
  offset?: number;
}

interface CreatorAccount {
  id: string;
  username: string;
  platform: string;
  follower_count: number | null;
  video_count: number | null;
  avatar_url: string | null;
  is_verified: boolean | null;
  claim_status: string | null;
  profile_url: string | null;
  bio: string | null;
  is_linked: boolean;
}

export interface GroupedCreator {
  id: string; // creator_group_id or single creator id
  primary_name: string;
  display_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  total_followers: number;
  is_verified: boolean;
  platforms: string[];
  creators: CreatorAccount[];
  claim_status: string; // 'unclaimed' | 'pending' | 'approved'
  is_grouped: boolean;
  is_fully_linked: boolean; // true if ALL accounts in group are linked
  unlinked_count: number; // count of unlinked accounts
}

interface GroupedCreatorsStats {
  totalGroups: number;
  totalCreators: number;
  byPlatform: {
    tiktok: number;
    instagram: number;
    youtube: number;
  };
  byClaim: {
    unclaimed: number;
    pending: number;
    approved: number;
  };
  byLinkStatus: {
    linked: number;
    unlinked: number;
  };
}

export function useGroupedCreatorsData(filters: GroupedCreatorFilters = {}) {
  const queryClient = useQueryClient();
  const { platform = '', searchTerm = '', claimStatus = '', limit = 20, offset = 0 } = filters;

  // Fetch all creators and group them
  const { data: creatorsData, isLoading, refetch } = useQuery({
    queryKey: ['grouped-creators-admin', platform, searchTerm, claimStatus, limit, offset],
    queryFn: async () => {
      // Fetch all creators with their group info
      let query = supabase
        .from('creators')
        .select('*')
        .order('follower_count', { ascending: false, nullsFirst: false });

      const { data: allCreators, error } = await query;
      
      if (error) throw error;
      
      // Fetch creator groups
      const { data: groups, error: groupsError } = await supabase
        .from('creator_groups')
        .select('*');
      
      if (groupsError) throw groupsError;

      // Build grouped creators
      const groupedMap = new Map<string, GroupedCreator>();
      const ungroupedCreators: CreatorAccount[] = [];

      // Process creators
      for (const creator of allCreators || []) {
        const account: CreatorAccount = {
          id: creator.id,
          username: creator.username,
          platform: creator.platform,
          follower_count: creator.follower_count,
          video_count: creator.video_count,
          avatar_url: creator.avatar_url,
          is_verified: creator.is_verified,
          claim_status: creator.claim_status,
          profile_url: creator.profile_url,
          bio: creator.bio,
          is_linked: creator.is_linked ?? true, // Default to true for backward compatibility
        };

        if (creator.creator_group_id) {
          const groupId = creator.creator_group_id;
          if (!groupedMap.has(groupId)) {
            const group = groups?.find(g => g.id === groupId);
            groupedMap.set(groupId, {
              id: groupId,
              primary_name: group?.primary_name || creator.display_name || creator.username,
              display_name: group?.display_name || creator.display_name,
              avatar_url: group?.avatar_url || creator.avatar_url,
              bio: group?.bio || creator.bio,
              total_followers: 0,
              is_verified: group?.is_verified || false,
              platforms: [],
              creators: [],
              claim_status: 'unclaimed',
              is_grouped: true,
              is_fully_linked: true,
              unlinked_count: 0,
            });
          }
          const grouped = groupedMap.get(groupId)!;
          grouped.creators.push(account);
          grouped.total_followers += account.follower_count || 0;
          if (!grouped.platforms.includes(account.platform)) {
            grouped.platforms.push(account.platform);
          }
          if (account.is_verified) grouped.is_verified = true;
          if (account.claim_status === 'approved') {
            grouped.claim_status = 'approved';
          } else if (account.claim_status === 'pending' && grouped.claim_status !== 'approved') {
            grouped.claim_status = 'pending';
          }
          // Track link status
          if (!account.is_linked) {
            grouped.is_fully_linked = false;
            grouped.unlinked_count++;
          }
        } else {
          ungroupedCreators.push(account);
        }
      }

      // Convert ungrouped creators to single-creator groups
      for (const creator of ungroupedCreators) {
        const isLinked = creator.is_linked ?? true;
        groupedMap.set(`single-${creator.id}`, {
          id: creator.id,
          primary_name: creator.username,
          display_name: null,
          avatar_url: creator.avatar_url,
          bio: creator.bio,
          total_followers: creator.follower_count || 0,
          is_verified: creator.is_verified || false,
          platforms: [creator.platform],
          creators: [{
            ...creator,
            is_linked: isLinked,
          }],
          claim_status: creator.claim_status || 'unclaimed',
          is_grouped: false,
          is_fully_linked: isLinked,
          unlinked_count: isLinked ? 0 : 1,
        });
      }

      let result = Array.from(groupedMap.values());

      // Apply filters
      if (platform) {
        result = result.filter(g => g.platforms.includes(platform));
      }

      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        result = result.filter(g => 
          g.primary_name.toLowerCase().includes(search) ||
          g.display_name?.toLowerCase().includes(search) ||
          g.creators.some(c => c.username.toLowerCase().includes(search))
        );
      }

      if (claimStatus && claimStatus !== 'all') {
        result = result.filter(g => g.claim_status === claimStatus);
      }

      // Sort by total followers
      result.sort((a, b) => b.total_followers - a.total_followers);

      // Calculate stats before pagination
      const totalUnlinkedCreators = result.reduce((sum, g) => sum + g.unlinked_count, 0);
      const stats: GroupedCreatorsStats = {
        totalGroups: result.length,
        totalCreators: result.reduce((sum, g) => sum + g.creators.length, 0),
        byPlatform: {
          tiktok: result.filter(g => g.platforms.includes('tiktok')).length,
          instagram: result.filter(g => g.platforms.includes('instagram')).length,
          youtube: result.filter(g => g.platforms.includes('youtube')).length,
        },
        byClaim: {
          unclaimed: result.filter(g => g.claim_status === 'unclaimed').length,
          pending: result.filter(g => g.claim_status === 'pending').length,
          approved: result.filter(g => g.claim_status === 'approved').length,
        },
        byLinkStatus: {
          linked: result.filter(g => g.is_fully_linked).length,
          unlinked: result.filter(g => !g.is_fully_linked).length,
        },
      };

      // Apply pagination
      const paginated = result.slice(offset, offset + limit);

      return { groups: paginated, total: result.length, stats };
    },
  });

  // Delete a single creator or group
  const deleteCreatorGroup = useMutation({
    mutationFn: async ({ id, isGrouped }: { id: string; isGrouped: boolean }) => {
      if (isGrouped) {
        // Delete all creators in the group first
        const { error: creatorsError } = await supabase
          .from('creators')
          .delete()
          .eq('creator_group_id', id);
        if (creatorsError) throw creatorsError;

        // Then delete the group
        const { error: groupError } = await supabase
          .from('creator_groups')
          .delete()
          .eq('id', id);
        if (groupError) throw groupError;
      } else {
        // Delete single creator
        const { error } = await supabase
          .from('creators')
          .delete()
          .eq('id', id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['grouped-creators-admin'] });
    },
  });

  // Bulk delete groups
  const bulkDeleteGroups = useMutation({
    mutationFn: async (groups: { id: string; isGrouped: boolean }[]) => {
      for (const group of groups) {
        if (group.isGrouped) {
          await supabase.from('creators').delete().eq('creator_group_id', group.id);
          await supabase.from('creator_groups').delete().eq('id', group.id);
        } else {
          await supabase.from('creators').delete().eq('id', group.id);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['grouped-creators-admin'] });
    },
  });

  // Delete all creators (optionally filtered by platform)
  const deleteAllCreators = useMutation({
    mutationFn: async (platformFilter?: string) => {
      if (platformFilter) {
        // Get creator IDs for this platform
        const { data: platformCreators } = await supabase
          .from('creators')
          .select('id, creator_group_id')
          .eq('platform', platformFilter);

        if (platformCreators) {
          // Delete creators
          const { error } = await supabase
            .from('creators')
            .delete()
            .eq('platform', platformFilter);
          if (error) throw error;

          // Clean up empty groups
          const groupIds = [...new Set(platformCreators
            .filter(c => c.creator_group_id)
            .map(c => c.creator_group_id))];

          for (const groupId of groupIds) {
            const { data: remaining } = await supabase
              .from('creators')
              .select('id')
              .eq('creator_group_id', groupId);
            
            if (!remaining || remaining.length === 0) {
              await supabase.from('creator_groups').delete().eq('id', groupId);
            }
          }
        }
      } else {
        // Delete all
        const { error: creatorsError } = await supabase.from('creators').delete().neq('id', '');
        if (creatorsError) throw creatorsError;
        
        const { error: groupsError } = await supabase.from('creator_groups').delete().neq('id', '');
        if (groupsError) throw groupsError;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['grouped-creators-admin'] });
    },
  });

  return {
    creatorGroups: creatorsData?.groups || [],
    total: creatorsData?.total || 0,
    stats: creatorsData?.stats || {
      totalGroups: 0,
      totalCreators: 0,
      byPlatform: { tiktok: 0, instagram: 0, youtube: 0 },
      byClaim: { unclaimed: 0, pending: 0, approved: 0 },
      byLinkStatus: { linked: 0, unlinked: 0 },
    },
    isLoading,
    refetch,
    deleteCreatorGroup,
    bulkDeleteGroups,
    deleteAllCreators,
  };
}
